import api from './api'

// Curated, agenda-scoped TB programme news & updates. News is admin-curated
// and tightly scoped to the TB-elimination agenda (NTEP, Ni-Kshay, PM TB Mukt
// Bharat Abhiyaan, Nikshay Poshan Yojana). Items reference real official
// sources — no fabricated statistics, named individuals or content.

export type NewsCategory =
  | 'PROGRAMME' | 'SCHEME' | 'POLICY' | 'COMMUNITY' | 'GUIDELINE' | 'ANNOUNCEMENT'

export type ResourceStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'

export const NEWS_CATEGORY_KEYS: NewsCategory[] = [
  'PROGRAMME', 'SCHEME', 'POLICY', 'COMMUNITY', 'GUIDELINE', 'ANNOUNCEMENT',
]

export const NEWS_CATEGORY_LABELS: Record<NewsCategory, string> = {
  PROGRAMME: 'Programme',
  SCHEME: 'Scheme',
  POLICY: 'Policy',
  COMMUNITY: 'Community',
  GUIDELINE: 'Guideline',
  ANNOUNCEMENT: 'Announcement',
}

export const NEWS_STATUS_LABELS: Record<ResourceStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
}

export interface NewsItem {
  id: number
  title: string
  slug: string
  summary?: string
  /** Legacy alias for sourceOrg. */
  source?: string
  /** Legacy alias for externalUrl (the official source URL). */
  url?: string
  imageUrl?: string | null
  category: NewsCategory
  isPinned?: boolean
  sortOrder?: number
  status?: ResourceStatus
  publishedAt?: string
  createdAt?: string
}

export interface NewsRequest {
  title: string
  slug?: string
  summary?: string
  sourceOrg?: string
  externalUrl?: string
  imageUrl?: string | null
  category: NewsCategory
  isPinned?: boolean
  sortOrder?: number
  status?: ResourceStatus
}

export interface NewsStats {
  TOTAL: number
  DRAFT: number
  PUBLISHED: number
  ARCHIVED: number
}

export interface NewsPage {
  content: NewsItem[]
  totalElements: number
  totalPages: number
}

/** Public curated news (paged) — used by the /news page. */
export async function listNews(params: { category?: string; q?: string; page?: number; size?: number } = {}): Promise<NewsPage> {
  const res = await api.get('/news', { params: { category: params.category, q: params.q, page: params.page ?? 0, size: params.size ?? 10 } })
  const d = res.data?.data ?? res.data
  return { content: d.content ?? [], totalElements: d.totalElements ?? 0, totalPages: d.totalPages ?? 0 }
}

/** Curated top-N for the non-dominating homepage "News & Updates" strip. */
export async function getTopNews(size = 3): Promise<NewsItem[]> {
  try {
    const res = await api.get(`/news/top?size=${size}`)
    return res.data?.data ?? []
  } catch (error) {
    console.error('Failed to fetch top news:', error)
    return []
  }
}

/** Backward-compatible flat list (used by the social feed's News tab). */
export async function getNews(page: number = 0, size: number = 10): Promise<NewsItem[]> {
  try {
    const res = await api.get(`/news?page=${page}&size=${size}`)
    const d = res.data?.data ?? res.data
    return d.content ?? d ?? []
  } catch (error) {
    console.error('Failed to fetch news:', error)
    return []
  }
}

export async function getNewsBySlug(slug: string): Promise<NewsItem> {
  const res = await api.get(`/news/${slug}`)
  return res.data?.data ?? res.data
}

// ---- admin ----
export async function listNewsAdmin(params: { status?: string; category?: string; q?: string; page?: number; size?: number } = {}): Promise<NewsPage> {
  const res = await api.get('/news/admin', { params: { status: params.status, category: params.category, q: params.q, page: params.page ?? 0, size: params.size ?? 20 } })
  const d = res.data?.data ?? res.data
  return { content: d.content ?? [], totalElements: d.totalElements ?? 0, totalPages: d.totalPages ?? 0 }
}

export async function getNewsStats(): Promise<NewsStats> {
  const res = await api.get('/news/stats')
  return res.data?.data ?? res.data
}

export async function createNews(req: NewsRequest): Promise<NewsItem> {
  const res = await api.post('/news', req)
  return res.data?.data ?? res.data
}

export async function updateNews(id: number, req: NewsRequest): Promise<NewsItem> {
  const res = await api.put(`/news/${id}`, req)
  return res.data?.data ?? res.data
}

export async function changeNewsStatus(id: number, status: ResourceStatus): Promise<NewsItem> {
  const res = await api.put(`/news/${id}/status`, null, { params: { status } })
  return res.data?.data ?? res.data
}

export async function deleteNews(id: number): Promise<void> {
  await api.delete(`/news/${id}`)
}

// Default object (kept for the social feed's `import newsService` usage).
const newsService = { getNews }
export default newsService